import { EventCardTemplate } from "./components/EventCardTemplate";
import { useCustom, useCustomMutation, useNavigation } from "@refinedev/core";
import { useParams } from "react-router-dom";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Switch,
  Typography,
  message,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { apiUrl } from "@/consts";
import { ImageUpload } from "@/components/ImageUpload";
import ClubTeamSelect, { type ClubTeamValue } from "./components/ClubTeamSelect";
import AddressSelect, { type AddressDTO } from "./components/AddressSelect";

const { Title, Text } = Typography;

type TicketCurrency = "eur" | "usd" | "gbp";

type ScoutingEvent = {
  playerCardTemplateKey: string | null;
  id: string;
  title: string;
  location: string;
  startTime: string;
  description: string | null;
  status: "DRAFT" | "ACTIVE" | "COMPLETED" | "CANCELLED";
  promoVideoUrl: string | null;
  onboardingHeadline: string | null;
  onboardingBlurb: string | null;
  ticketLabel: string | null;
  ticketDescription: string | null;
  ticketPriceCents: number | null;
  ticketCurrency: TicketCurrency;
  ticketStripeProductId: string | null;
  ticketStripePriceId: string | null;
  ticketEnabled: boolean;
  publicRegistrationEnabled: boolean;
  media: { id: string; url: string | null } | null;
  clubId: string | null;
  teamId: string | null;
  club?: { id: string; name: string } | null;
  team?: { id: string; name: string } | null;
  addressId: string | null;
  address?: (AddressDTO & { id: string }) | null;
};

export function ScoutingEventEdit() {
  const { id } = useParams<{ id: string }>();
  const { show } = useNavigation();
  const [form] = Form.useForm();
  const [templateBusy, setTemplateBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const { mutate } = useCustomMutation();

  const { data, isLoading } = useCustom<{ event: ScoutingEvent }>({
    url: `${apiUrl}/scouting-event/${id}`,
    method: "get",
    queryOptions: { enabled: !!id },
  });

  const event = data?.data?.event;

  const [clubTeam, setClubTeam] = useState<ClubTeamValue>({
    clubId: null,
    teamId: null,
  });
  const [venue, setVenue] = useState<AddressDTO | null>(null);

  // Pre-populate the club/team attachment once the event loads.
  useEffect(() => {
    if (event) {
      setClubTeam({ clubId: event.clubId ?? null, teamId: event.teamId ?? null });
    }
  }, [event?.clubId, event?.teamId]);

  // Seed the venue from the event's persisted address so saving without
  // touching it re-sends the same address (the backend dedupes by place id).
  useEffect(() => {
    if (event?.address) setVenue(event.address);
  }, [event?.addressId]);

  function handleFinish(values: any) {
    if (templateBusy) { message.warning("Wait for the player card preview to finish"); return; }
    if (!venue) {
      message.error("Please select a venue address");
      return;
    }
    setSaving(true);
    const priceEuros = values.ticketPriceEuros;
    const ticketPriceCents =
      priceEuros == null || priceEuros === ""
        ? null
        : Math.round(Number(priceEuros) * 100);

    mutate(
      {
        url: `${apiUrl}/scouting-event/${id}`,
        method: "put",
        values: {
          playerCardTemplateKey: values.playerCardTemplateKey ?? null,
          title: values.title,
          address: venue,
          startTime: values.startTime?.toISOString?.() ?? values.startTime,
          description: values.description ?? null,
          status: values.status,
          promoVideoUrl:
            typeof values.promoVideoUrl === "string" && values.promoVideoUrl.trim()
              ? values.promoVideoUrl.trim()
              : null,
          onboardingHeadline: values.onboardingHeadline?.trim?.() || null,
          onboardingBlurb: values.onboardingBlurb?.trim?.() || null,
          ticketEnabled: !!values.ticketEnabled,
          publicRegistrationEnabled: !!values.publicRegistrationEnabled,
          ticketLabel: values.ticketLabel?.trim?.() || null,
          ticketDescription: values.ticketDescription?.trim?.() || null,
          ticketPriceCents,
          ticketCurrency: values.ticketCurrency ?? "eur",
          clubId: clubTeam.clubId,
          teamId: clubTeam.teamId,
          ...(values.mediaID ? { mediaID: values.mediaID } : {}),
        },
      },
      {
        onSuccess: () => {
          setSaving(false);
          show("scouting-event", id!);
        },
        onError: () => {
          setSaving(false);
        },
      }
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!event) {
    return <Text type="danger">Event not found.</Text>;
  }

  return (
    <div style={{ maxWidth: 640, padding: "24px 8px" }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        Edit Scouting Event
      </Title>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          playerCardTemplateKey: event.playerCardTemplateKey,
          title: event.title,
          startTime: dayjs(event.startTime),
          description: event.description ?? undefined,
          status: event.status,
          promoVideoUrl: event.promoVideoUrl ?? "",
          onboardingHeadline: event.onboardingHeadline ?? "",
          onboardingBlurb: event.onboardingBlurb ?? "",
          ticketEnabled: event.ticketEnabled,
          publicRegistrationEnabled: event.publicRegistrationEnabled,
          ticketLabel: event.ticketLabel ?? "",
          ticketDescription: event.ticketDescription ?? "",
          ticketCurrency: event.ticketCurrency ?? "eur",
          ticketPriceEuros:
            event.ticketPriceCents != null ? event.ticketPriceCents / 100 : null,
        }}
        onFinish={handleFinish}
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Location" required help="Search and pick the venue address.">
          <AddressSelect
            id="scoutingVenue"
            value={venue}
            onChange={setVenue}
            initialLabel={event.address?.formattedAddress ?? event.location}
          />
        </Form.Item>

        <Form.Item
          label="Start Time"
          name="startTime"
          rules={[{ required: true, message: "Start time is required" }]}
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          label="Promo Video URL"
          name="promoVideoUrl"
          extra="YouTube or Vimeo link shown as the hero on the event landing page."
          rules={[{ type: "url", message: "Must be a valid URL" }]}
        >
          <Input placeholder="https://www.youtube.com/watch?v=..." />
        </Form.Item>

        <Form.Item label="Status" name="status">
          <Select>
            <Select.Option value="DRAFT">DRAFT</Select.Option>
            <Select.Option value="ACTIVE">ACTIVE</Select.Option>
            <Select.Option value="COMPLETED">COMPLETED</Select.Option>
            <Select.Option value="CANCELLED">CANCELLED</Select.Option>
          </Select>
        </Form.Item>

      <Divider orientation="left">Player card template</Divider>
      <Form.Item name="playerCardTemplateKey">
        <EventCardTemplate onBusyChange={setTemplateBusy} />
      </Form.Item>

        <Divider orientation="left">Club & team</Divider>
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Attach this event to a club and one of its teams. Choose a club to enable the
          team select; clearing the club detaches the team.
        </Text>
        <Form.Item>
          <ClubTeamSelect
            value={clubTeam}
            onChange={(next) => {
              setClubTeam(next);
              if (!next.clubId) form.setFieldValue("publicRegistrationEnabled", false);
            }}
            initialClubName={event.club?.name}
            initialTeamName={event.team?.name}
          />
        </Form.Item>

        <Form.Item
          name="publicRegistrationEnabled"
          label="Public club testing registration"
          valuePropName="checked"
          extra="Anyone can register without a club membership. Active upcoming events appear on the club website. Enable tickets below to include checkout."
        >
          <Switch disabled={!clubTeam.clubId} />
        </Form.Item>
        <Divider orientation="left">Tickets & onboarding</Divider>
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Changing the price or currency mints a fresh Stripe product and price
          on the platform account; the new IDs are written back to the event.
        </Text>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Ticket sales enabled"
              name="ticketEnabled"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item label="Ticket label" name="ticketLabel">
              <Input placeholder="Scout Trial Ticket — Dublin" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item label="Currency" name="ticketCurrency">
              <Select
                options={[
                  { value: "eur", label: "EUR" },
                  { value: "usd", label: "USD" },
                  { value: "gbp", label: "GBP" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item label="Price" name="ticketPriceEuros">
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                placeholder="29.00"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Ticket description" name="ticketDescription">
          <Input.TextArea rows={2} placeholder="Shown on the ticket page and in Stripe" />
        </Form.Item>

        <Form.Item label="Onboarding headline" name="onboardingHeadline">
          <Input placeholder="Hero copy on the event landing page" />
        </Form.Item>

        <Form.Item label="Onboarding blurb" name="onboardingBlurb">
          <Input.TextArea rows={3} placeholder="Supporting paragraph below the headline" />
        </Form.Item>

        {(event.ticketStripeProductId || event.ticketStripePriceId) && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Current Stripe IDs (read-only):
            </Text>
            <div style={{ marginTop: 4, fontFamily: "monospace", fontSize: 12 }}>
              {event.ticketStripeProductId && (
                <div>product: {event.ticketStripeProductId}</div>
              )}
              {event.ticketStripePriceId && (
                <div>price: {event.ticketStripePriceId}</div>
              )}
            </div>
          </div>
        )}

        <Divider orientation="left">Cover image</Divider>

        <Form.Item label="Cover Image" name="mediaID">
          <ImageUpload uploadType="SCOUTING_EVENT" />
        </Form.Item>

        {event.media?.url && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Current media:
            </Text>
            <div style={{ marginTop: 8 }}>
              <img
                src={event.media.url}
                alt="current cover"
                style={{ maxWidth: 200, borderRadius: 4 }}
              />
            </div>
          </div>
        )}

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={saving}
            disabled={saving || templateBusy}
          >
            Save Changes
          </Button>
          <Button
            style={{ marginLeft: 12 }}
            onClick={() => show("scouting-event", id!)}
          >
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
